import { ColumnDef } from '@tanstack/react-table'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Button } from 'reactstrap'
import documents from '../../../api/documents/hooks'
import { DocumentFile } from '../../../api/documents/types'
import { downloadFile } from '../../../api/globalFunctions'
import { MODAL_TYPES } from '../../../common/constants'
import { getSVGIconFromExtension } from '../../../utils/files'
import { formatDateTime } from '../../../utils/general'
import AppPermissionsSwitcher from '../../AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../AppPermissionsSwitcher/constants/actions'
import { setDeleteCallback } from '../../Modals/DeleteModal/utils'
import { showModal } from '../../../store/modal'


export const useTableColumns = (
    documentModule: string | undefined,
    onSync: (id: number) => void,
    onEdit: (row: DocumentFile) => void
): ColumnDef<DocumentFile>[] => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const { mutate: deleteDocument } = documents.DeleteDocument.useMutation()

    const handleDownload = useCallback(async (rowData: DocumentFile) => {
        const currentLang = localStorage.getItem('i18nextLng') || 'hr'
        const fileDescription = rowData.translations?.[currentLang]?.fileDescription ?? rowData.translations?.hr?.fileDescription
        const fileName = `${fileDescription}.${rowData.fileExtension}`

        await downloadFile(String(rowData.id), fileName)
    }, [])

    const handleDelete = useCallback(
        (id: number) => {
            const callbackId = 'delete-document'
            const payload = {
                modalType: MODAL_TYPES.DELETE_MODAL,
                modalProps: { callbackId, id },
            }

            setDeleteCallback(callbackId, () => deleteDocument(id))
            dispatch(showModal(payload))
        },
        [dispatch, deleteDocument]
    )

    return useMemo(
        (): ColumnDef<DocumentFile>[] => [
            {
                header: t('app.common.id'),
                id: 'id',
                accessorKey: 'internalAppId',
                enableSorting: false,
                size: 40,
            },
            {
                header: t('table.header.documents.fileName'),
                id: 'fileName',
                enableSorting: false,
                cell: ({ row }) => {
                    const { translations, fileExtension } = row.original
                    const currentLang = localStorage.getItem('i18nextLng') || 'hr'
                    const fileDescription = translations?.[currentLang]?.fileDescription ?? translations?.hr?.fileDescription

                    return (
                        <>
                            {getSVGIconFromExtension(fileExtension)}
                            <span className="d-inline-block me-2" />
                            {`${fileDescription ?? ''}.${fileExtension}`}
                        </>
                    )
                },
            },
            {
                header: t('app.common.documentType'),
                id: 'documentType',
                accessorFn: (row) => row.documentType?.name ?? null,
                enableSorting: false,
            },
            ...(!documentModule
                ? ([
                      {
                          header: t('app.common.contactCompany'),
                          id: 'documentContactId',
                          enableSorting: false,
                          size: 90,
                          cell: ({ row }) => (
                              <>{row.original.module === 'contact' || row.original.module === 'company' ? row.original.parentElement?.title : ''}</>
                          ),
                      },
                      {
                          header: t('app.common.propertyId'),
                          id: 'documentPropertyId',
                          enableSorting: false,
                          size: 70,
                          cell: ({ row }) => (
                              <>{row.original.module === 'property' ? row.original.parentElement?.element?.internalAppId : ''}</>
                          ),
                      },
                      {
                          header: t('app.common.projectId'),
                          id: 'documentProjectId',
                          enableSorting: false,
                          size: 55,
                          cell: ({ row }) => (
                              <>{row.original.module === 'project' ? row.original.parentElement?.element?.internalAppId : ''}</>
                          ),
                      },
                  ] as ColumnDef<DocumentFile>[])
                : []),
            {
                header: t('app.common.creationDate'),
                id: 'documentCreationDate',
                enableSorting: false,
                size: 80,
                cell: ({ row }) => formatDateTime(row.original.documentCreationDate),
            },
            {
                header: t('app.common.expirationDate'),
                id: 'documentValidityDate',
                enableSorting: false,
                size: 75,
                cell: ({ row }) => formatDateTime(row.original.documentValidityDate),
            },
            {
                id: 'actions',
                enableSorting: false,
                meta: { tdClass: 'text-center' },
                cell: ({ row }) => {
                    const agent = row.original.agent
                    const itemAgentId = agent?.id
                    const itemAgentOfficeId = agent?.agencyOffice?.id

                    return (
                        <ul className="list-inline hstack gap-2 mb-0 justify-content-center">
                            <li className="list-inline-item">
                                <Button
                                    title={t('app.common.synchronize')}
                                    color="link"
                                    className={`${row.original.fileSync ? 'text-success' : 'text-secondary'} d-inline-block p-0`}
                                    data-testid="document-sync-btn"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        onSync(row.original.id)
                                    }}
                                >
                                    <i className={`mdi ${row.original.fileSync ? 'mdi-check-circle' : 'mdi-check-circle-outline'} fs-16`}></i>
                                </Button>
                            </li>
                            <li className="list-inline-item">
                                <Button
                                    title={t('button.download')}
                                    color="link"
                                    className="text-warning d-inline-block p-0"
                                    data-testid="document-download-btn"
                                    onClick={() => handleDownload(row.original)}
                                >
                                    <i className="mdi mdi-download fs-16"></i>
                                </Button>
                            </li>
                            <AppPermissionsSwitcher
                                permission="ModulePermissions"
                                itemAgentId={itemAgentId}
                                itemAgentOfficeId={itemAgentOfficeId}
                                module={'documents'}
                                action={action.edit}
                            >
                                {({ hasModulePermission }: { hasModulePermission: boolean }) =>
                                    hasModulePermission && (
                                        <>
                                            <li className="list-inline-item">
                                                <Button
                                                    title={t('button.edit')}
                                                    color="link"
                                                    className="text-info d-inline-block p-0"
                                                    data-testid="document-edit-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault()
                                                        onEdit(row.original)
                                                    }}
                                                >
                                                    <i className="mdi mdi-pencil-outline fs-16"></i>
                                                </Button>
                                            </li>
                                            <li className="list-inline-item">
                                                <Button
                                                    title={t('button.delete')}
                                                    color="link"
                                                    className="text-danger d-inline-block p-0"
                                                    data-testid="document-delete-btn"
                                                    onClick={() => handleDelete(row.original.id)}
                                                >
                                                    <i className="mdi mdi-trash-can-outline fs-16"></i>
                                                </Button>
                                            </li>
                                        </>
                                    )
                                }
                            </AppPermissionsSwitcher>
                        </ul>
                    )
                },
            },
        ],
        [documentModule, onSync, onEdit, handleDownload, handleDelete, t]
    )
}
